import React, { useState, useEffect, useRef } from "react"
import _get from "lodash/get"
import { useDispatch, useSelector } from "react-redux"
import useSearchi18n from "@/i18n/useSearchi18n"

import {
  INITIAL_SEARCH_TABS_COUNT,
  PRODUCTS_PER_PAGE,
  SEARCH_TABS,
} from "@/constants/index"
import { getConfig } from "@/constants/config"
import {
  addToRecentSearch,
  isShowAnalyticsEventLoaded,
  getShortenedUrl,
  getParamFromUrl,
  removeSearchQueryParams,
  getBadgeValue,
} from "@/utils/helper"
import { getBrandSwitcher } from "@/utils"
import {
  fetchSearchAllResults,
  fetchSearchServicePartsResults,
  searchState,
  clearSearchResults,
  setSearchKeyword as setSearchKeywordStore,
  setActiveTabDispatch,
} from "@/store/features/searchSlice"

import SearchViewComponent from "@/components/Search/v1/SearchView"
import InspirationTab from "@/components/Search/v1/InspirationTab"
import TechnicalTab from "@/components/Search/v1/TechnicalTab/index"
import ProductsTab from "@/components/Search/v1/ProductsTab"
import OuterSearchProgressive from "@/components/Search/v1/OuterSearchProgressive"

import {
  selectProductListState,
  clearProductData,
} from "@/store/features/productListSlice"
import { addSearchAnalytics } from "@/components/Default/DynamicSearch/v1/analytics"

const SearchComponent = props => {
  const dispatch = useDispatch()
  const newState = useSelector(searchState)
  const { plp: { total: filteredTotalResults = 0 } = {} } = useSelector(
    selectProductListState
  )
  const { searchResponse, searchDetails } = newState
  const productsPerPage = PRODUCTS_PER_PAGE
  const { data: componentProp, header: headerProps } = props
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState({})
  const [partsData, setPartsData] = useState({})
  const [searchKeyword, setSearchKeyword] = useState("")
  const [searchMainKeyword, setSearchMainKeyword] = useState("")
  const [loading, setLoading] = useState(true)
  const [exactMatch, setExactMatch] = useState(false)
  const [tagDebug, setTagDebug] = useState(true)
  const [searchClickEvent, setSearchClickEvent] = useState(false)
  const [debounceInProgress, setDebounceInProgress] = useState(false)
  const [isFromServicePart, setIsFromServicePart] = useState(false)
  const [general, setGeneral] = useState()
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  // eslint-disable-next-line no-unused-vars
  const [info, setInfo] = useState({})
  const [loadPopularSearch, setLoadPopularSearch] = useState(false)
  const [isSterling, setIsSterling] = useState(false)
  const [plpBadge, setPlpBadge] = useState({})
  const [searchClear, setSearchClear] = useState(null)
  const [searchTagger, setSearchTagger] = useState(null)

  const [tabResultCounts, setTabResultCounts] = useState(
    INITIAL_SEARCH_TABS_COUNT
  )

  const pageLoadedRef = useRef(false)

  const staticTexts = useSearchi18n({
    componentProp,
  })

  const brandSwitcherItems = getBrandSwitcher(headerProps)

  useEffect(() => {
    getConfig().then(async config => {
      const { plpConfigurations: { myArrayList: plpBadgeList = [] } = {} } =
        config
      const brandName = config?.general?.siteName ?? ""
      const locale = _get(config, "internationalization.locale", "")
      const currencySign = _get(config, "internationalization.currencySign", "")
      const currencyCode = _get(config, "internationalization.currencyCode", "")
      const knowledgeArticleUrl = await getShortenedUrl(
        _get(config, "general.knowledgeArticlePath", "")
      )
      const marketParamValues = _get(config, "marketParamValues")

      const { general: { lwAppName = "" } = {} } = config ?? {}

      const info = {
        locale: locale,
        currencySign: currencySign,
        currencyCode: currencyCode,
        knowledgeArticleUrl: knowledgeArticleUrl,
        lwAppName,
      }

      const displayOrderBadges =
        plpBadgeList.find(item => item.map.Key === "displayOrderBadges") ?? {}
      const {
        map: { Values: { myArrayList: displayOrderBadgeList = [] } = {} } = {},
      } = displayOrderBadges

      const plpBadges = {}
      displayOrderBadgeList.forEach(item => {
        plpBadges[item] = getBadgeValue(item, plpBadgeList)
      })
      const general = {
        brandName: brandName,
        marketParamValues: marketParamValues,
        lwAppName: lwAppName,
      }
      setInfo(info)
      setGeneral(general)
      setPlpBadge({
        plpBadges,
        displayOrderBadgeList,
      })
      setTimeout(function () {
        setIsInitialLoad(false)
      }, 2000)
    }, [])
    try {
      const urlParams = new URL(window?.location?.href)
      if (urlParams?.searchParams?.get("keyword")) {
        const keyword = decodeURIComponent(
          urlParams?.searchParams?.get("keyword")
        )
        const isFromServicePart = decodeURIComponent(
          urlParams?.searchParams.get("service")
        )

        if (keyword?.length < 3) {
          setSearchKeyword(keyword)
          dispatch(setSearchKeywordStore(keyword))
          setLoadPopularSearch(true)
          setLoading(false)
        } else {
          setSearchKeyword(keyword)
          dispatch(setSearchKeywordStore(keyword))
          setIsFromServicePart(isFromServicePart === "true")
          setLoading(true)
        }
      } else {
        setLoadPopularSearch(true)
        setLoading(false)
      }
    } catch (error) {
      setLoadPopularSearch(true)
      setLoading(false)
      console.error("URIError: malformed URI sequence")
    }
    return () => {
      sessionStorage?.removeItem("previousPageNameForSearch")
    }
  }, [])
  const tabList = []
  let activeTab

  useEffect(() => {
    if (
      filteredTotalResults > 0 &&
      activeTab >= 0 &&
      activeTab < tabList.length
    ) {
      const newTabCounts = {
        ...tabResultCounts,
        [tabList[activeTab].id]: filteredTotalResults,
      }

      setTabResultCounts(newTabCounts)

      const newTotal = Object.values(newTabCounts).reduce(
        (acc, curr) => acc + curr,
        0
      )

      setTotal(newTotal)
    }
  }, [filteredTotalResults, tabList.length, activeTab])

  useEffect(() => {
    const fullcatalogResponse =
      searchResponse?.data?.fusion?.full_catalog_Response
    if (fullcatalogResponse?.length) {
      if (fullcatalogResponse[0]["Brand_ss"][0] === "Sterling") {
        setIsSterling(true)
      }
    } else {
      setIsSterling(false)
    }
  }, [searchResponse])

  useEffect(() => {
    if (general) {
      if (searchKeyword?.length > 1 && isInitialLoad) {
        getResults(
          searchKeyword,
          general?.brandName?.toLowerCase(),
          searchClear,
          searchTagger
        )?.catch(() => {})
        setSearchMainKeyword(searchKeyword)
        removeSearchQueryParams()
        setIsInitialLoad(false)
      }
    }
  }, [searchKeyword, tagDebug, general])

  const handleSearch = (keyword, clear, tagger) => {
    if (searchDetails.keyword !== keyword) {
      dispatch(setActiveTabDispatch(0))
    }
    setSearchKeyword(keyword)
    setTagDebug(!tagger)
    setDebounceInProgress(true)
    setSearchClear(clear)
    setSearchTagger(tagger)
    dispatch(setSearchKeywordStore(keyword))

    if (keyword?.length === 0 || keyword?.length === 1) {
      setTotal(0)
      setLoadPopularSearch(true)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" })
      }, 50)
      setUrlParam(keyword)
    } else if (keyword?.length > 1) {
      // Clear old search and product data before starting new search
      dispatch(clearSearchResults())
      dispatch(clearProductData())
      setLoading(true)

      // Call getResults directly with the clear and tagger values
      getResults(
        keyword,
        general?.brandName?.toLowerCase(),
        clear,
        tagger
      )?.catch(() => {})
      setSearchMainKeyword(keyword)
      removeSearchQueryParams()
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }
  const handleSearchEnter = () => {
    if (debounceInProgress || loading) {
      setSearchClickEvent(true)
    } else {
      if (searchResponse?.data?.fusion?.redirect?.[0]) {
        window.location.href = searchResponse?.data?.fusion?.redirect[0]
        addSearchAnalytics(
          {
            action: "cmp:click",
            status: "success",
            total: 1,
            autoSearch: false,
            clear: searchClear,
            tagger: searchTagger,
          },
          general,
          searchKeyword
        )
        // this means no any requests are in progress. just add click event directly
      }
      addSearchAnalytics(
        {
          action: "cmp:click",
          status: "success",
          total: total,
          autoSearch: false,
          clear: searchClear,
          tagger: searchTagger,
        },
        general,
        searchKeyword
      )
    }
  }
  const setUrlParam = keyword => {
    if (keyword) {
      sessionStorage.setItem("previousUrl", window?.location?.href)
      const url = new URL(window?.location?.href)
      const searchParams = url?.searchParams
      let urlSearch = "?" + searchParams?.toString()
      if (searchParams.get("keyword") !== keyword?.trim()) {
        urlSearch = `?keyword=${encodeURIComponent(keyword)}`
      }

      // Debounce URL updates to prevent Safari crashes
      if (window.__urlUpdateTimeout) {
        clearTimeout(window.__urlUpdateTimeout)
      }
      window.__urlUpdateTimeout = setTimeout(() => {
        try {
          window?.history?.replaceState(
            window?.location?.pathname,
            document?.title,
            urlSearch
          )
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("Failed to update URL:", err.message)
        }
      }, 150) // 150ms debounce
    }
  }
  const getResults = (
    keyword,
    brandNameVal = "kohler",
    clear = null,
    tagger = null
  ) => {
    const { disableParts } = staticTexts
    return new Promise((resolve, reject) => {
      setLoading(true)
      setDebounceInProgress(false)
      setLoadPopularSearch(false)
      let params = {
        q: keyword,
        rows: 1,
        "lw.tagger.debug": tagDebug,
        collections: brandNameVal,
        displaytype: "search",
      }
      if (isFromServicePart) {
        params = {
          q: keyword,
          rows: 1,
          "lw.tagger.debug": tagDebug,
          collections: brandNameVal,

          service: true,
          displaytype: "search",
        }
      }

      Promise.all([
        dispatch(fetchSearchAllResults({ ...params })),

        dispatch(
          fetchSearchServicePartsResults({
            q: keyword,
            collections: "service_parts",
            displaytype: "search",
          })
        ),
      ])
        .then(resp => {
          const firstResponse = resp[0]
          const firstRespPayload = firstResponse?.payload
          const firstRespFusion = firstRespPayload?.fusion
          const fullCatalogResponse = firstRespFusion?.full_catalog_Response
          let isSterlingTemp = false
          const { redirect: brandFusionRedirect = [] } = firstRespFusion
          if (brandFusionRedirect && brandFusionRedirect.length > 0) {
            window.location.href = brandFusionRedirect[0]
            return
          }
          if (fullCatalogResponse?.length) {
            if (fullCatalogResponse[0]["Brand_ss"][0] === "Sterling") {
              isSterlingTemp = true
              setIsSterling(true)
            }
          }
          const isShowEvent = isShowAnalyticsEventLoaded()
          const isClickEvent = searchClickEvent
          if (resp[0]?.payload && resp[1]?.payload) {
            addToRecentSearch(keyword)
            let groups = [
              {
                key: "products",
                count: _get(
                  resp[0]?.payload,
                  "facet_counts.facet_fields.Products[1]",
                  0
                ),
              },
              {
                key: "parts",
                count: _get(resp[1]?.payload, "response.numFound", 0),
              },
              {
                key: "technical",
                count: _get(
                  resp[0]?.payload,
                  "facet_counts.facet_fields.Technical[1]",
                  0
                ),
              },
              {
                key: "inspiration",
                count: _get(
                  resp[0]?.payload,
                  "facet_counts.facet_fields.Inspiration[1]",
                  0
                ),
              },
              {
                key: "resource",
                count: _get(
                  resp[0]?.payload,
                  "facet_counts.facet_fields.Resource[1]",
                  0
                ),
              },
              {
                key: "help",
                count: _get(
                  resp[0]?.payload,
                  "facet_counts.facet_fields.Help[1]",
                  0
                ),
              },
            ]
            if (disableParts) {
              groups = groups?.filter(arr => {
                return arr?.key !== "parts"
              })
            }
            // Calculating the total results
            let total = 0
            const selected = groups?.find(group => group?.count > 0)
            if (
              resp[0]?.payload?.fusion?.exactMatch ||
              resp[1]?.payload?.fusion?.exactMatch
            ) {
              total =
                selected?.key === "parts"
                  ? resp[1]?.payload?.response?.numFound
                  : resp[0]?.payload?.response?.numFound
            } else {
              groups?.map(group => (total += group?.count))
            }
            if (total === 0) {
              setLoadPopularSearch(true)
            }
            setLoading(false)
            // setData(resp[0].payload)
            setPartsData(resp[1]?.payload)
            setTotal(total)
            setSelected(selected)
            setExactMatch(
              selected?.key === "parts"
                ? resp[1]?.payload?.fusion?.exactMatch
                : resp[0]?.payload?.fusion?.exactMatch
            )
            setSearchClickEvent(false)
            setUrlParam(keyword)
            const productTabCount = _get(
              resp[0].payload,
              "facet_counts.facet_fields.Products[1]",
              0
            )
            const inspireTabCount = _get(
              resp[0].payload,
              "facet_counts.facet_fields.Inspiration[1]",
              0
            )

            const technicalTabCount = _get(
              resp[0].payload,
              "facet_counts.facet_fields.Technical[1]",
              0
            )
            const resourceTabCount = _get(
              resp[0].payload,
              "facet_counts.facet_fields.Resource[1]",
              0
            )
            const helpTabCount = _get(
              resp[0].payload,
              "facet_counts.facet_fields.Help[1]",
              0
            )
            const partTabCount = _get(resp[1].payload, "response.numFound", 0)
            const tabCountObj = {
              productTabCount: isSterlingTemp ? 1 : productTabCount,
              helpTabCount,
              resourceTabCount,
              technicalTabCount,
              inspireTabCount,
              partTabCount,
            }
            setTabResultCounts({
              [SEARCH_TABS.PRODUCTS]: productTabCount,
              [SEARCH_TABS.PARTS]: partTabCount,
              [SEARCH_TABS.INSPIRATION]: inspireTabCount,
              [SEARCH_TABS.TECHNICAL]: technicalTabCount,
              [SEARCH_TABS.RESOURCES]: resourceTabCount,
              [SEARCH_TABS.HELP]: helpTabCount,
            })
            addSearchAnalytics(
              {
                action: isClickEvent ? "cmp:click" : "cmp:show",
                status: "success",
                total,
                autoSearch: !isClickEvent && isShowEvent,
                keyword: keyword,
                tabCountObj,
                isSterlingTemp,
                urlFlag: true,
                clear: clear,
                tagger: tagger,
              },
              general,
              searchKeyword
            )

            // if the url changes on subsequent searches then log a cmp:show as well
            if (pageLoadedRef.current) {
              addSearchAnalytics(
                {
                  action: "cmp:show",
                  status: "success",
                  total,
                  autoSearch: false,
                  keyword: keyword,
                  tabCountObj,
                  isSterlingTemp,
                  searchCountIncreased: false,
                  clear: clear,
                  tagger: tagger,
                },
                general,
                searchKeyword
              )
            } else {
              pageLoadedRef.current = true
            }
            resolve()
          }
        })
        ?.catch(err => {
          // eslint-disable-next-line no-console
          console.log("Error occured while fetching Search data", err)
          const isShowEvent = isShowAnalyticsEventLoaded()
          const isClickEvent = searchClickEvent
          setLoading(false)
          setSearchClickEvent(false)
          setLoadPopularSearch(true)
          addSearchAnalytics(
            {
              action: isClickEvent ? "cmp:click" : "cmp:show",
              errorMessage: staticTexts?.noResults?.toLowerCase(),
              status: "failure",
              total: 0,
              autoSearch: !isClickEvent && isShowEvent,
              clear: clear,
              tagger: tagger,
            },
            general,
            searchKeyword
          )
          reject(new Error("something bad happened"))
        })
    })
  }
  const staticData = staticTexts
  const { disableParts } = staticData

  if (
    _get(searchResponse?.data, "facet_counts.facet_fields.Products", [])
      ?.length > 1 ||
    (isSterling &&
      searchResponse?.data?.fusion?.full_catalog_Response?.length > 0)
  ) {
    const count = isSterling ? 1 : tabResultCounts[SEARCH_TABS.PRODUCTS]
    tabList?.push({
      id: SEARCH_TABS.PRODUCTS,
      title: staticTexts?.products,
      count: count,
      content: (
        <ProductsTab
          staticTexts={staticTexts}
          page="search"
          key={SEARCH_TABS.PRODUCTS}
          type={SEARCH_TABS.PRODUCTS}
          searchKeyword={searchMainKeyword}
          totalSearchResults={isSterling ? 1 : total}
          count={count}
          currencySign={info?.currencySign}
          onSearch={handleSearch}
          query={'Products:("ct")'}
          lwAppName={general?.lwAppName}
          componentProps={componentProp}
          presetConfigs={props?.data?.presetConfigs}
          marketParamValues={general?.marketParamValues}
          isSterling={isSterling}
          sterlingData={
            isSterling && searchResponse.data.fusion.full_catalog_Response
          }
          showSterlingButton={true}
          brandSwitcherItems={brandSwitcherItems}
          plpBadge={plpBadge}
          brand={general?.brandName}
          quickViewAuthData={props.quickViewAuthData}
        />
      ),
    })
  }
  if (!isSterling) {
    if (_get(partsData, "response.numFound", 0) > 0 && !disableParts) {
      const count = tabResultCounts[SEARCH_TABS.PARTS]
      tabList?.push({
        id: SEARCH_TABS.PARTS,
        title: staticTexts?.parts,
        count: count,
        content: (
          <ProductsTab
            staticTexts={staticTexts}
            page="search"
            key={SEARCH_TABS.PARTS}
            type={SEARCH_TABS.PARTS}
            count={count}
            searchKeyword={searchMainKeyword}
            totalSearchResults={total}
            currencySign={info?.currencySign}
            onSearch={handleSearch}
            productsPerPage={productsPerPage}
            componentProps={componentProp}
            presetConfigs={props?.data?.presetConfigs}
            marketParamValues={general?.marketParamValues}
            isSterling={isSterling}
            sterlingData={
              isSterling && searchResponse.data.fusion.full_catalog_Response
            }
            lwAppName={general?.lwAppName}
            brand={general?.brandName}
            quickViewAuthData={props.quickViewAuthData}
          />
        ),
      })
    }
    if (
      _get(searchResponse?.data, "facet_counts.facet_fields.Inspiration", [])
        ?.length > 1
    ) {
      const count = tabResultCounts[SEARCH_TABS.INSPIRATION]
      tabList?.push({
        id: SEARCH_TABS.INSPIRATION,
        title: staticTexts.inspiration,
        count: count,
        content: (
          <InspirationTab
            page="search"
            key={SEARCH_TABS.INSPIRATION}
            type={SEARCH_TABS.INSPIRATION}
            searchKeyword={searchMainKeyword}
            totalResults={count}
            totalSearchResults={total}
            displayShare={true}
            displayPrint={true}
            onSearch={handleSearch}
            productsPerPage={productsPerPage}
            fquery={'Inspiration:("Inspiration")'}
            staticTexts={staticTexts}
            knowledgeArticleUrl={info?.knowledgeArticleUrl}
            marketParamValues={general?.marketParamValues}
            brand={general?.brandName}
          />
        ),
      })
    }
    if (
      _get(searchResponse?.data, "facet_counts.facet_fields.Technical", [])
        ?.length > 1
    ) {
      const count = tabResultCounts[SEARCH_TABS.TECHNICAL]
      tabList?.push({
        id: SEARCH_TABS.TECHNICAL,
        title: staticTexts?.technical,
        count: count,
        content: (
          <TechnicalTab
            page="search"
            staticTexts={staticTexts}
            key={SEARCH_TABS.TECHNICAL}
            type={SEARCH_TABS.TECHNICAL}
            count={count}
            searchKeyword={searchMainKeyword}
            totalSearchResults={total}
            displayShare={true}
            displayPrint={true}
            onSearch={handleSearch}
            productsPerPage={productsPerPage}
            totalResults={total}
            marketParamValues={general?.marketParamValues}
            brand={general?.brandName}
          />
        ),
      })
    }
    if (
      _get(searchResponse?.data, "facet_counts.facet_fields.Resource", [])
        ?.length > 1
    ) {
      const count = tabResultCounts[SEARCH_TABS.RESOURCES]
      tabList?.push({
        id: SEARCH_TABS.RESOURCES,
        title: staticTexts?.resource,
        count: count,
        content: (
          <InspirationTab
            page="search"
            key={SEARCH_TABS.RESOURCES}
            type={SEARCH_TABS.RESOURCES}
            searchKeyword={searchMainKeyword}
            totalResults={count}
            totalSearchResults={total}
            displayShare={true}
            displayPrint={true}
            onSearch={handleSearch}
            productsPerPage={productsPerPage}
            fquery={'Resource:("Resource")'}
            staticTexts={staticTexts}
            knowledgeArticleUrl={info.knowledgeArticleUrl}
            brand={general?.brandName}
          />
        ),
      })
    }
    if (
      _get(searchResponse?.data, "facet_counts.facet_fields.Help", [])?.length >
      1
    ) {
      const count = tabResultCounts[SEARCH_TABS.HELP]
      tabList?.push({
        id: SEARCH_TABS.HELP,
        title: staticTexts?.help,
        count: count,
        content: (
          <InspirationTab
            page="search"
            staticTexts={staticTexts}
            key={SEARCH_TABS.HELP}
            type={SEARCH_TABS.HELP}
            searchKeyword={searchMainKeyword}
            totalResults={count}
            onSearch={handleSearch}
            productsPerPage={productsPerPage}
            fquery={'Help:("sf")'}
            knowledgeArticleUrl={info.knowledgeArticleUrl}
            totalSearchResults={total}
            displayShare={true}
            displayPrint={true}
            brand={general?.brandName}
          />
        ),
      })
    }
  }

  const currentTab = getParamFromUrl("tab") || ""

  if (currentTab) {
    activeTab = tabList?.findIndex(tab => tab?.id === currentTab)
  } else if (
    searchDetails?.activeTab !== undefined &&
    searchDetails?.activeTab >= 0 &&
    searchDetails?.activeTab < tabList.length
  ) {
    activeTab = searchDetails.activeTab
  } else {
    const isDiscontinued =
      _get(searchResponse?.data, "fusion.disContinuedCollection", "") === "true"

    const productsFacet = _get(
      searchResponse?.data,
      "facet_counts.facet_fields.Products"
    )

    const hasProducts = productsFacet && productsFacet.length > 0

    if (isDiscontinued && !hasProducts) {
      const technicalTabIndex = tabList?.findIndex(
        tab => tab?.id === SEARCH_TABS.TECHNICAL
      )

      activeTab = technicalTabIndex >= 0 ? technicalTabIndex : 0
    } else {
      const productsTabIndex = tabList?.findIndex(
        tab => tab?.id === SEARCH_TABS.PRODUCTS
      )

      const defaultTab = _get(searchResponse?.data, "fusion.default_tab[0]", "")

      const defaultTabIndex = defaultTab
        ? tabList?.findIndex(tab => tab?.id === defaultTab)
        : -1

      activeTab =
        productsTabIndex >= 0
          ? productsTabIndex
          : defaultTabIndex >= 0
          ? defaultTabIndex
          : 0
    }
  }

  if (exactMatch)
    tabList?.forEach((tab, index) => {
      if (tab?.id !== selected?.key) {
        tabList?.splice(1, index)
      }
    })

  if ((searchResponse?.data || searchKeyword?.length < 2) && !loading) {
    return (
      <>
        <SearchViewComponent
          activeTabVal={activeTab}
          loading={loading}
          data={componentProp}
          staticTexts={staticTexts}
          totalResults={isSterling ? 1 : total}
          searchKeyword={searchKeyword}
          tabList={tabList}
          selected={selected}
          exactMatch={exactMatch}
          onSearch={handleSearch}
          brand={general?.brandName}
          handleSearchEnter={handleSearchEnter}
          staticData={staticData}
          loadPopularSearch={loadPopularSearch}
          searchResponse={searchResponse}
          isSterling={isSterling}
        />
      </>
    )
  } else {
    return <OuterSearchProgressive />
  }
}

export default SearchComponent
